import { Base64 } from 'js-base64';

import { ExtractedContextSchema, ExtractedContextV2Schema } from '../shared/types';
import type { ExtractedContext, ExtractedContextV2 } from '../shared/types';

/**
 * Encodes ExtractedContext to base64url for storage in mention elements.
 * Uses JSON.stringify + base64 encoding to preserve all data.
 */
export function encodeExtractedContext(context: ExtractedContext): string {
  const json = JSON.stringify(context);
  return Base64.encodeURL(json);
}

/**
 * Decodes base64url-encoded ExtractedContext back to object.
 * Returns null if decoding fails (e.g., legacy format from before ExtractedContext migration).
 */
export function decodeExtractedContext(encoded: string): ExtractedContext | null {
  try {
    const json = Base64.decode(encoded);
    const parsed: unknown = JSON.parse(json);
    const result = ExtractedContextSchema.safeParse(parsed);
    if (!result.success) return null;
    return result.data;
  } catch {
    return null;
  }
}

/**
 * Encodes v2 selection context (carries focusSuggestionId alongside the
 * paragraph extraction). Same base64url scheme as v1 — only the data shape
 * differs.
 */
export function encodeExtractedContextV2(context: ExtractedContextV2): string {
  const json = JSON.stringify(context);
  return Base64.encodeURL(json);
}

/**
 * Decodes base64url-encoded ExtractedContextV2. Returns null on decode
 * failure or when the payload doesn't carry the v2 shape.
 */
export function decodeExtractedContextV2(encoded: string): ExtractedContextV2 | null {
  try {
    const json = Base64.decode(encoded);
    const parsed: unknown = JSON.parse(json);
    const result = ExtractedContextV2Schema.safeParse(parsed);
    if (!result.success) return null;
    return result.data;
  } catch {
    return null;
  }
}
